import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AcademicCapIcon,
  CalendarIcon,
  XMarkIcon,
  ClockIcon,
  TrophyIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { API_BASE_URL } from '../../../config/api';
import PeriodComparisonComponent from '../../../components/analytics/PeriodComparisonComponent';
import { getTodayKST } from '../../../utils/dateUtils';
// 🔴 회원 관리 표와 **같은 배지**를 쓴다 (2026-09-05 대표 지시).
//    따로 만들면 같은 학생인데 화면마다 상태가 다르게 보인다.
import { getStatusBadge, getMembershipBadge } from '../utils/memberBadges';

// ==========================================
// Types
// ==========================================

interface Student {
  id: string;
  name: string;
  member_number?: string;
  memberNumber?: string;
  // 목록을 회원 관리와 같은 표로 보여주기 위한 필드 (2026-09-05)
  status?: any;
  member_category?: string | null;
  membershipType?: string;
  remainingTime?: number | null;
  expiryDate?: string | null;
  voucherName?: string;
  phone?: string | null;
  assigned_seat?: any;
  assigned_seat_code?: string | null;
}

interface DailyChartItem {
  date: string;
  hours: number;
}

interface WeeklyAchievementItem {
  week: string;
  rate: number | null;
}

interface AnalyticsSummary {
  this_week_hours: number;
  this_week_days: number;
  achievement_rate: number | null;
  consecutive_days: number;
  daily_chart: DailyChartItem[];
  weekly_achievement: WeeklyAchievementItem[];
}

// ==========================================
// Component
// ==========================================

const StudyAnalyticsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState<Student[]>([]);
  /* 🔴 고른 학생을 **주소에 담는다** (2026-09-05 대표 지시).
        종전엔 화면 안 상태로만 들고 있어서 주소가 늘 같았다. 그래서
        브라우저·휴대폰 **뒤로가기를 누르면 목록이 아니라 대시보드로 튕겼다**
        (실측 확인). 모바일에서 뒤로가기를 자주 쓰는데 매번 메뉴부터 다시
        들어와야 했다. 주소에 담으면 뒤로가기가 목록으로 돌아오고,
        새로고침·즐겨찾기로 그 학생 화면을 그대로 다시 열 수 있다. */
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedStudentId = searchParams.get('student');
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [lastSearchedTerm, setLastSearchedTerm] = useState('');
  const [totalStudents, setTotalStudents] = useState(0);

  // 학생 고르기 / 목록으로 돌아가기 — 주소를 함께 바꾼다
  const selectStudent = (student: Student) => {
    setSelectedStudent(student);
    setSearchTerm('');
    setSearchParams({ student: student.id });
  };
  const backToList = () => {
    setSelectedStudent(null);
    setSearchParams({});
  };

  // 시간 표시 헬퍼 함수
  const formatTime = (timeString: string | null | undefined): string => {
    if (!timeString) return '-';
    return timeString;
  };

  // 분석 데이터
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [studyTimeData, setStudyTimeData] = useState<any>(null);
  const [selectedDate, setSelectedDate] = useState<string>(getTodayKST());
  const [sessionsOpen, setSessionsOpen] = useState(false);

  // 원장 코멘트 (2026-09-05 부천신중동점 요청) — 월 단위로 한 줄
  const [commentPeriod, setCommentPeriod] = useState(() => getTodayKST().slice(0, 7));
  const [comment, setComment] = useState('');
  const [commentSaved, setCommentSaved] = useState('');   // 저장된 원본 (바뀌었는지 비교용)
  const [commentLoading, setCommentLoading] = useState(false);
  const [commentSaving, setCommentSaving] = useState(false);
  const [commentUpdatedAt, setCommentUpdatedAt] = useState<string | null>(null);

  // 누가 코멘트를 썼는지 목록에 표시 (2026-09-06 부천신중동점 요청)
  // 학생을 하나씩 눌러보지 않아도 목록에서 바로 갈린다.
  const [commentedIds, setCommentedIds] = useState<Set<string>>(new Set());
  const [onlyUncommented, setOnlyUncommented] = useState(false);

  // 리포트 공유 링크 (2026-09-05) — 알림톡 버튼으로 보낼 주소. 지금은 원장이 복사해 쓴다.
  const [shareUrl, setShareUrl] = useState('');
  const [shareLoading, setShareLoading] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  // ==========================================
  // 데이터 로드
  // ==========================================

  const searchStudents = useCallback(async (query: string) => {
    try {
      setSearchLoading(true);
      const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
      const headers = { Authorization: `Bearer ${token}` };

      // 🔴 명단을 화면에 통째로 보여주므로 넉넉히 받는다 (2026-09-05).
      //    20명만 받으면 20명 넘는 매장에서 명단이 반쪽만 나온다.
      const url = query
        ? `${API_BASE_URL}/v1/owner/members?search=${encodeURIComponent(query)}&limit=300`
        : `${API_BASE_URL}/v1/owner/members?limit=300`;

      const response = await fetch(url, { headers });

      if (!response.ok) {
        console.error('학생 검색 실패:', response.status);
        return;
      }

      const data = await response.json();
      setStudents(data.members || []);
      setTotalStudents(data.total || 0);
      setLastSearchedTerm(query);
    } catch (error) {
      console.error('학생 검색 실패:', error);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  // 초기 로드
  useEffect(() => {
    const loadInitial = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
        const headers = { Authorization: `Bearer ${token}` };

        const response = await fetch(`${API_BASE_URL}/v1/owner/members?limit=300`, { headers });

        if (response.ok) {
          const data = await response.json();
          setStudents(data.members || []);
          setTotalStudents(data.total || 0);
        }
      } catch (error) {
        console.error('초기 학생 목록 로드 실패:', error);
      } finally {
        setLoading(false);
      }
    };
    loadInitial();
  }, []);

  // 디바운스 검색
  useEffect(() => {
    const timer = setTimeout(() => {
      searchStudents(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, searchStudents]);

  // 주소에 학생이 담긴 채로 들어왔을 때(새로고침·즐겨찾기·뒤로가기) 그 학생을 채운다
  useEffect(() => {
    if (!selectedStudentId) {
      setSelectedStudent(null);
      return;
    }
    if (selectedStudent?.id === selectedStudentId) return;
    const found = students.find(s => s.id === selectedStudentId);
    if (found) setSelectedStudent(found);
  }, [selectedStudentId, selectedStudent, students]);

  // ── 원장 코멘트 ────────────────────────────────────────────────
  const authHeaders = () => {
    const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  };

  useEffect(() => {
    if (!selectedStudentId) {
      setComment(''); setCommentSaved(''); setCommentUpdatedAt(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setCommentLoading(true);
        const res = await fetch(
          `${API_BASE_URL}/v1/owner/study/report-comment` +
          `?student_id=${encodeURIComponent(selectedStudentId)}&period_key=${commentPeriod}`,
          { headers: authHeaders() }
        );
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        setComment(data.comment || '');
        setCommentSaved(data.comment || '');
        setCommentUpdatedAt(data.updated_at || null);
      } catch (e) {
        console.error('코멘트 불러오기 실패:', e);
      } finally {
        if (!cancelled) setCommentLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedStudentId, commentPeriod]);

  // 그 달에 코멘트를 쓴 학생 목록 — 한 번에 받아 온다 (학생마다 물어보지 않는다)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/v1/owner/study/report-comment/written?period_key=${commentPeriod}`,
          { headers: authHeaders() }
        );
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        setCommentedIds(new Set<string>(data.student_ids || []));
      } catch (e) {
        // 실패해도 목록 자체는 보여야 한다 — 표시만 비운다
        console.error('코멘트 작성 현황 불러오기 실패:', e);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commentPeriod]);

  const saveComment = async () => {
    if (!selectedStudentId) return;
    try {
      setCommentSaving(true);
      const res = await fetch(
        `${API_BASE_URL}/v1/owner/study/report-comment`,
        {
          method: 'PUT',
          headers: authHeaders(),
          body: JSON.stringify({
            student_id: selectedStudentId,
            period_key: commentPeriod,
            comment,
          }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert(data.detail || '코멘트를 저장하지 못했습니다.');
        return;
      }
      setCommentSaved(data.comment || '');
      setComment(data.comment || '');
      setCommentUpdatedAt(data.updated_at || null);
      // 목록의 표시도 바로 따라간다 — 저장했는데 '미작성'으로 남아 있으면 원장이 또 쓴다.
      // 내용을 비워 저장하면 지워지므로(서버 동작) 그 경우엔 표시도 뺀다.
      setCommentedIds(prev => {
        const next = new Set(prev);
        if ((data.comment || '').trim()) next.add(selectedStudentId);
        else next.delete(selectedStudentId);
        return next;
      });
    } catch (e) {
      console.error('코멘트 저장 실패:', e);
      alert('코멘트를 저장하지 못했습니다.');
    } finally {
      setCommentSaving(false);
    }
  };

  // 학생이나 달이 바뀌면 이전 링크는 지운다 — 엉뚱한 학생 주소를 복사하지 않도록
  useEffect(() => {
    setShareUrl('');
    setShareCopied(false);
  }, [selectedStudentId, commentPeriod]);

  const makeShareLink = async () => {
    if (!selectedStudentId) return;
    try {
      setShareLoading(true);
      const res = await fetch(`${API_BASE_URL}/v1/owner/study/report-link`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ student_id: selectedStudentId, period_key: commentPeriod }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert(data.detail || '링크를 만들지 못했습니다.');
        return;
      }
      setShareUrl(data.url || '');
      setShareCopied(false);
    } catch (e) {
      console.error('리포트 링크 생성 실패:', e);
      alert('링크를 만들지 못했습니다.');
    } finally {
      setShareLoading(false);
    }
  };

  const copyShareLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2500);
    } catch {
      // 클립보드가 막힌 환경(구형 브라우저·권한 거부)에서도 주소는 화면에 보이므로
      // 원장이 직접 긁어 복사할 수 있다. 여기서 실패해도 흐름을 막지 않는다.
      alert('자동 복사가 막혀 있습니다. 아래 주소를 직접 복사해 주세요.');
    }
  };

  // 최근 6개월 — 지난달 리포트를 보낼 수도 있어 기간을 고르게 한다
  const periodOptions = React.useMemo(() => {
    const out: string[] = [];
    const base = new Date(getTodayKST() + 'T00:00:00');
    for (let i = 0; i < 6; i++) {
      const d = new Date(base.getFullYear(), base.getMonth() - i, 1);
      out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    }
    return out;
  }, []);

  // 학생 선택 시 데이터 로드
  useEffect(() => {
    if (selectedStudentId) {
      loadSummary(selectedStudentId);
      loadStudyTimeData(selectedStudentId, selectedDate);
    }
  }, [selectedStudentId]);

  // 날짜 변경 시 세션 데이터만 다시 로드
  useEffect(() => {
    if (selectedStudentId) {
      loadStudyTimeData(selectedStudentId, selectedDate);
    }
  }, [selectedDate]);

  const loadSummary = async (studentId: string) => {
    try {
      const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
      const headers = { Authorization: `Bearer ${token}` };

      const response = await fetch(
        `${API_BASE_URL}/v1/study-time/student/${studentId}/analytics-summary`,
        { headers }
      );

      if (response.ok) {
        const data = await response.json();
        setSummary(data);
      } else {
        setSummary(null);
      }
    } catch (error) {
      console.error('분석 요약 로드 실패:', error);
      setSummary(null);
    }
  };

  const loadStudyTimeData = async (studentId: string, date: string) => {
    try {
      const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
      const headers = { Authorization: `Bearer ${token}` };

      const studyTimeUrl = `${API_BASE_URL}/api/my/study-time/today?user_id=${studentId}&date=${date}`;
      const response = await fetch(studyTimeUrl, { headers });
      if (response.ok) {
        const data = await response.json();
        setStudyTimeData(data);
      } else {
        setStudyTimeData(null);
      }
    } catch (error) {
      console.error('순공부시간 조회 실패:', error);
      setStudyTimeData(null);
    }
  };

  // ==========================================
  // UI Helpers
  // ==========================================

  const getAchievementColor = (rate: number | null) => {
    if (rate === null) return 'text-gray-400';
    if (rate >= 80) return 'text-green-600';
    if (rate >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getAchievementBg = (rate: number | null) => {
    if (rate === null) return 'bg-gray-50';
    if (rate >= 80) return 'bg-green-50';
    if (rate >= 60) return 'bg-yellow-50';
    return 'bg-red-50';
  };

  // 차트용 날짜 포맷 (M/D)
  const formatChartDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return `${d.getMonth() + 1}/${d.getDate()}`;
  };

  // ==========================================
  // 렌더링
  // ==========================================

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-gray-500 mb-2">로딩 중...</div>
          <div className="text-sm text-gray-400">학생 목록을 불러오는 중입니다</div>
        </div>
      </div>
    );
  }

  if ((!students || students.length === 0) && !searchTerm && totalStudents === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">학습 분석</h1>
          <p className="text-gray-500 mt-1">학생의 공부시간과 달성률을 분석하세요</p>
        </div>
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <AcademicCapIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 mb-2">등록된 학생이 없습니다</p>
          <p className="text-sm text-gray-400">학생을 먼저 등록해주세요</p>
        </div>
      </div>
    );
  }

  // '아직 안 쓴 학생만' 을 켜면 목록을 좁힌다 (2026-09-06)
  const filteredStudents = onlyUncommented
    ? students.filter(s => !commentedIds.has(s.id))
    : students;
  const commentedCount = students.filter(s => commentedIds.has(s.id)).length;
  const commentMonthLabel = `${Number(commentPeriod.slice(5, 7))}월`;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* 헤더 */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">학습 분석</h1>
        <p className="text-gray-500 mt-1">학생의 공부시간과 달성률을 분석하세요</p>
      </div>

      {/* 1단계: 학생 선택 - 컴팩트 디자인 */}
      <div className="mb-6">
        {selectedStudent ? (
          /* 학생이 선택된 경우: 컴팩트한 바 형태 */
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 font-bold">
                    {selectedStudent.name.charAt(0)}
                  </span>
                </div>
                <div>
                  <div className="font-semibold text-gray-900">
                    {selectedStudent.name}
                  </div>
                  <div className="text-xs text-gray-500">
                    {selectedStudent.memberNumber || selectedStudent.member_number}
                  </div>
                </div>
              </div>
              <button
                onClick={() => {
                  backToList();
                  setSummary(null);
                  setStudyTimeData(null);
                }}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                ← 목록으로
              </button>
            </div>
          </div>
        ) : (
          /* 학생이 선택되지 않은 경우: 검색 UI */
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4">
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="flex-1 min-w-0 relative">
                <input
                  type="text"
                  placeholder="학생 이름 또는 회원번호로 검색..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={async (e) => {
                    if (e.key === 'Enter' && !e.nativeEvent.isComposing && searchTerm.trim()) {
                      const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
                      try {
                        const res = await fetch(
                          `${API_BASE_URL}/v1/owner/members?search=${encodeURIComponent(searchTerm.trim())}&limit=5`,
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        if (res.ok) {
                          const data = await res.json();
                          const members = data.members || [];
                          if (members.length > 0) {
                            const exact = members.find((m: any) => m.name === searchTerm.trim());
                            const s = exact || members[0];
                            selectStudent(s);
                          }
                        }
                      } catch {}
                    }
                  }}
                  className="w-full px-4 py-2.5 pr-10 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                />

                {searchLoading ? (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" />
                  </div>
                ) : searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    type="button"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                )}

              </div>

              <span className="text-sm text-gray-500 whitespace-nowrap">
                {searchTerm ? `${students.length}명` : `총 ${totalStudents}명`}
              </span>
            </div>

            {/* 학생 명단 — 검색어를 치지 않아도 바로 보인다 (2026-09-05 부천신중동점 요청)
                종전엔 검색창에 뭔가 입력해야만 목록이 떠서, 원장이 이름을 정확히 알아야
                했고 화면은 늘 비어 있었다. 검색은 이제 이 명단을 좁히는 용도다. */}
            {/* 🔴 회원 관리와 **같은 목록 형식**으로 보여준다 (2026-09-05 대표 지시).
                   카드로 늘어놓으니 원장이 늘 보던 화면과 달라 낯설었다.
                   행을 누르면 그 학생의 학습 분석으로 넘어간다. */}
            {/* 코멘트 작성 현황 요약 + 걸러 보기 (2026-09-06 부천신중동점 요청)
                "한번에 다 작성하는 것이 아니라서 누구를 했는지 한눈에 알 수 있으면" */}
            <div className="mt-3 flex flex-wrap items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
              {/* 🔴 검색 중이면 이 숫자는 '검색 결과' 기준이다 — 그렇다고 말해 준다.
                     안 밝히면 원장이 매장 전체 현황으로 읽는다. */}
              <span className="text-sm text-amber-900">
                ✍️ <span className="font-semibold">{commentMonthLabel}</span> 원장 코멘트
                {searchTerm && <span className="text-amber-700"> — 검색한 {students.length}명 중</span>}
                <span className="ml-1 font-semibold text-amber-900">{commentedCount}명</span>
                <span className="text-amber-700"> 작성 / </span>
                <span className="font-semibold text-amber-900">{students.length - commentedCount}명</span>
                <span className="text-amber-700"> 아직</span>
              </span>
              <button
                type="button"
                onClick={() => setOnlyUncommented(v => !v)}
                className={`ml-auto px-3 py-1 rounded border text-sm transition-colors ${
                  onlyUncommented
                    ? 'bg-amber-500 border-amber-500 text-white hover:bg-amber-600'
                    : 'bg-white border-amber-300 text-amber-800 hover:bg-amber-100'
                }`}
              >
                {onlyUncommented ? '전체 보기' : '아직 안 쓴 학생만'}
              </button>
            </div>

            <div className="mt-3 bg-white border border-gray-200 rounded-lg overflow-hidden">
              {filteredStudents.length === 0 ? (
                <div className="px-4 py-6 text-gray-500 text-center text-sm">
                  {onlyUncommented
                    ? `${commentMonthLabel} 코멘트를 모든 학생에게 쓰셨습니다 🎉`
                    : searchTerm ? '검색 결과가 없습니다' : '학생이 없습니다'}
                </div>
              ) : (
                <div className="max-h-[28rem] overflow-y-auto overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">이름</th>
                        {/* 🔴 휴대폰에서도 숨기지 않는다 — 이 화면에서 원장이 제일 보고 싶어하는 칸이다 */}
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">코멘트</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">상태</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">구분</th>
                        {/* 🔴 좁은 화면(휴대폰)에서는 이 두 칸을 숨긴다.
                            다 넣으면 표가 옆으로 잘려 이름조차 반쯤 가린다.
                            학생을 고르는 것이 목적이라 이름·상태·구분이면 충분하다. */}
                        <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">이용권</th>
                        <th className="hidden lg:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">좌석번호</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredStudents.map(student => (
                        <tr
                          key={student.id}
                          className="hover:bg-blue-50 cursor-pointer"
                          onClick={() => {
                            selectStudent(student);
                          }}
                        >
                          {/* 🔴 좁은 화면에서는 회원번호를 이름 **아래**로 내린다.
                              나란히 두면 이름 칸이 넓어져 뒤 칸들이 화면 밖으로 밀린다. */}
                          <td className="px-3 sm:px-4 py-3 whitespace-nowrap">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2">
                              <div className="text-sm font-medium text-gray-900">{student.name}</div>
                              <span className="inline-flex w-fit items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                {student.memberNumber || student.member_number || 'N/A'}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {commentedIds.has(student.id) ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                ✍️ 작성함
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">
                                미작성
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {student.status ? getStatusBadge(student.status) : <span className="text-sm text-gray-400">-</span>}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {student.member_category ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                                {student.member_category}
                              </span>
                            ) : (
                              <span className="text-sm text-gray-400">-</span>
                            )}
                          </td>
                          <td className="hidden md:table-cell px-4 py-3 whitespace-nowrap">
                            {getMembershipBadge(
                              student.membershipType as any,
                              student.remainingTime as any,
                              student.expiryDate as any,
                              student.voucherName as any
                            )}
                          </td>
                          <td className="hidden lg:table-cell px-4 py-3 whitespace-nowrap">
                            {student.assigned_seat ? (
                              <span className="text-sm font-medium text-purple-600">
                                {student.assigned_seat_code || `좌석 ${student.assigned_seat}`}
                              </span>
                            ) : (
                              <span className="text-sm text-gray-400">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* 학습 분석 콘텐츠 */}
      <div>
        {selectedStudent ? (
          <div className="space-y-4">

            {/* ========== 원장 코멘트 (2026-09-05 부천신중동점 요청) ==========
                알림톡 서식은 카카오 검수를 기다리는 중이라, 원장이 이 화면을 캡쳐해
                학부모에게 바로 보낼 수 있도록 한 마디 적는 칸을 먼저 열었다.
                기간은 월 단위 — 같은 달에는 한 줄만 남고 덮어쓴다. */}
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                  <AcademicCapIcon className="h-5 w-5 text-indigo-500" />
                  <span className="font-medium text-gray-900">원장 코멘트</span>
                  <select
                    value={commentPeriod}
                    onChange={(e) => setCommentPeriod(e.target.value)}
                    className="text-sm border border-gray-200 rounded px-2 py-1"
                  >
                    {periodOptions.map((pk) => {
                      const [y, m] = pk.split('-');
                      return <option key={pk} value={pk}>{`${y}년 ${Number(m)}월`}</option>;
                    })}
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  {commentUpdatedAt && (
                    <span className="text-xs text-gray-400">
                      저장됨 {new Date(commentUpdatedAt).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })}
                    </span>
                  )}
                  <button
                    onClick={saveComment}
                    disabled={commentSaving || commentLoading || comment === commentSaved}
                    className="px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700 disabled:opacity-40"
                  >
                    {commentSaving ? '저장 중…' : comment === commentSaved ? '저장됨' : '저장'}
                  </button>
                </div>
              </div>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                disabled={commentLoading}
                rows={3}
                maxLength={1000}
                placeholder={`${selectedStudent.name} 학생에 대해 학부모님께 전할 말을 적어주세요.\n이 화면을 그대로 캡쳐해 보내실 수 있습니다.`}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
              <div className="mt-1 flex items-center justify-between">
                <p className="text-xs text-gray-500">
                  이 달({(() => { const [y, m] = commentPeriod.split('-'); return `${y}년 ${Number(m)}월`; })()}) 코멘트로 저장됩니다. 학생마다 따로 남습니다.
                </p>
                <span className="text-xs text-gray-400">{comment.length}/1000</span>
              </div>

              {/* 학부모에게 보낼 리포트 주소 (2026-09-05)
                  알림톡 본문에는 긴 글을 못 넣는다(치환변수 14자 제한). 그래서 주소를 보내고
                  내용은 웹에서 보여준다. 자동 발송은 카카오 검수 뒤에 붙는다 — 그때까지는
                  원장이 이 주소를 복사해 직접 보낼 수 있다. */}
              <div className="mt-3 pt-3 border-t border-gray-100">
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={makeShareLink}
                    disabled={shareLoading}
                    className="px-3 py-1.5 text-sm font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded hover:bg-indigo-100 disabled:opacity-40"
                  >
                    {shareLoading ? '만드는 중…' : '📤 학부모에게 보낼 주소 만들기'}
                  </button>
                  {shareUrl && (
                    <>
                      <a
                        href={shareUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm text-indigo-600 underline break-all"
                      >
                        {shareUrl}
                      </a>
                      <button
                        onClick={copyShareLink}
                        className="px-2 py-1 text-xs font-medium text-white bg-gray-700 rounded hover:bg-gray-800"
                      >
                        {shareCopied ? '복사됨' : '복사'}
                      </button>
                    </>
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  이 주소를 카카오톡·문자로 보내시면 학부모님이 로그인 없이 이 달 리포트를 보십니다.
                  <span className="font-medium"> 받은 분이 다른 사람에게 전달하면 그분도 볼 수 있으니 </span>
                  주의해 주세요. 60일 뒤 만료됩니다.
                </p>
              </div>
            </div>

            {/* ========== 요약 카드 3개 ========== */}
            <div className="grid grid-cols-3 gap-4">
              {/* 이번 주 공부 */}
              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center gap-2 mb-2">
                  <ClockIcon className="h-5 w-5 text-blue-500" />
                  <span className="text-sm text-gray-500">이번 주 공부</span>
                </div>
                <p className="text-2xl font-bold text-blue-600">
                  {summary ? (
                    summary.this_week_hours >= 1
                      ? `${Math.floor(summary.this_week_hours)}시간 ${Math.round((summary.this_week_hours % 1) * 60)}분`
                      : `${Math.round(summary.this_week_hours * 60)}분`
                  ) : '-'}
                </p>
              </div>

              {/* 이번 주 출석 */}
              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CalendarIcon className="h-5 w-5 text-green-500" />
                  <span className="text-sm text-gray-500">이번 주 출석</span>
                </div>
                <p className="text-2xl font-bold text-green-600">
                  {summary ? `${summary.this_week_days}일` : '-'}
                </p>
              </div>

              {/* 달성률 */}
              <div className={`rounded-lg shadow p-4 ${summary ? getAchievementBg(summary.achievement_rate) : 'bg-white'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <TrophyIcon className="h-5 w-5 text-yellow-500" />
                  <span className="text-sm text-gray-500">달성률</span>
                </div>
                <p className={`text-2xl font-bold ${summary ? getAchievementColor(summary.achievement_rate) : 'text-gray-400'}`}>
                  {summary
                    ? summary.achievement_rate !== null
                      ? `${summary.achievement_rate}%`
                      : '목표 없음'
                    : '-'}
                </p>
              </div>

            </div>

            {/* ========== 차트 2개 (좌우 배치) ========== */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* 일별 공부시간 (최근 14일) */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <ClockIcon className="h-5 w-5 text-blue-600 mr-2" />
                  일별 공부시간
                  <span className="ml-2 text-sm font-normal text-gray-400">최근 14일</span>
                </h3>
                {summary && summary.daily_chart.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={summary.daily_chart}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="date"
                        tickFormatter={formatChartDate}
                        tick={{ fontSize: 11 }}
                      />
                      <YAxis
                        tick={{ fontSize: 11 }}
                        label={{ value: '시간', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
                      />
                      <Tooltip
                        formatter={(value: any) => [`${value}시간`, '공부시간']}
                        labelFormatter={(label: any) => {
                          const d = new Date(label);
                          return `${d.getMonth() + 1}월 ${d.getDate()}일`;
                        }}
                      />
                      <Bar dataKey="hours" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[250px] flex items-center justify-center text-gray-400">
                    데이터가 없습니다
                  </div>
                )}
              </div>

              {/* 주간 달성률 추이 (최근 8주) */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <TrophyIcon className="h-5 w-5 text-yellow-600 mr-2" />
                  주간 달성률 추이
                  <span className="ml-2 text-sm font-normal text-gray-400">최근 8주</span>
                </h3>
                {summary && summary.weekly_achievement.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={summary.weekly_achievement}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="week"
                        tick={{ fontSize: 10 }}
                      />
                      <YAxis
                        domain={[0, 100]}
                        tick={{ fontSize: 11 }}
                        label={{ value: '%', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
                      />
                      <Tooltip
                        formatter={(value: any) => {
                          if (value === null || value === undefined) return ['데이터 없음', '달성률'];
                          return [`${Number(value).toFixed(1)}%`, '달성률'];
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="rate"
                        stroke="#F59E0B"
                        strokeWidth={2}
                        dot={{ fill: '#F59E0B', r: 4 }}
                        connectNulls={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[250px] flex items-center justify-center text-gray-400">
                    목표가 설정된 주가 없습니다
                  </div>
                )}
              </div>
            </div>

            {/* ========== 기간 비교 분석 ========== */}
            {selectedStudentId && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <CalendarIcon className="h-6 w-6 text-purple-600 mr-2" />
                  기간 비교 분석
                </h3>
                <PeriodComparisonComponent studentId={selectedStudentId!} comparisonType="weekly" />
              </div>
            )}

            {/* ========== 오늘 학습 세션 (아코디언) ========== */}
            <div className="bg-white rounded-lg shadow">
              <button
                onClick={() => setSessionsOpen(!sessionsOpen)}
                className="w-full px-6 py-4 flex items-center justify-between text-left"
              >
                <div className="flex items-center">
                  <AcademicCapIcon className="h-5 w-5 text-blue-600 mr-2" />
                  <h3 className="text-lg font-semibold text-gray-900">
                    학습 세션
                  </h3>
                  <div className="flex items-center ml-4">
                    <input
                      type="date"
                      value={selectedDate}
                      onChange={(e) => {
                        e.stopPropagation();
                        setSelectedDate(e.target.value);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      max={getTodayKST()}
                      className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  {studyTimeData && (
                    <span className="ml-3 text-sm text-gray-500">
                      {studyTimeData.study_sessions?.length || 0}개 세션 /
                      {' '}{Math.floor(studyTimeData.pure_study_minutes / 60)}시간 {studyTimeData.pure_study_minutes % 60}분
                    </span>
                  )}
                </div>
                {sessionsOpen ? (
                  <ChevronUpIcon className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                )}
              </button>

              {sessionsOpen && (
                <div className="px-6 pb-6 border-t border-gray-100">
                  {/* 요약 카드 */}
                  <div className="grid grid-cols-3 gap-4 my-4">
                    <div className="text-center p-3 bg-blue-50 rounded-lg">
                      <p className="text-xs text-gray-500 mb-1">순공부시간</p>
                      <p className="text-lg font-bold text-blue-600">
                        {studyTimeData
                          ? `${Math.floor(studyTimeData.pure_study_minutes / 60)}시간 ${studyTimeData.pure_study_minutes % 60}분`
                          : '-'}
                      </p>
                    </div>
                    <div className="text-center p-3 bg-green-50 rounded-lg">
                      <p className="text-xs text-gray-500 mb-1">학습 세션</p>
                      <p className="text-lg font-bold text-green-600">
                        {studyTimeData ? `${studyTimeData.study_sessions?.length || 0}회` : '-'}
                      </p>
                    </div>
                    <div className="text-center p-3 bg-purple-50 rounded-lg">
                      <p className="text-xs text-gray-500 mb-1">집중률</p>
                      <p className="text-lg font-bold text-purple-600">
                        {studyTimeData ? `${studyTimeData.efficiency_rate}%` : '-%'}
                      </p>
                    </div>
                  </div>

                  {/* 세션 테이블 */}
                  {studyTimeData && studyTimeData.study_sessions && studyTimeData.study_sessions.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">입실</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">퇴실</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">순공부시간</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">상태</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {studyTimeData.study_sessions.map((session: any) => (
                            <tr key={session.session_id} className={session.status === 'excluded' ? 'bg-gray-50' : ''}>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{formatTime(session.check_in)}</td>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{formatTime(session.check_out)}</td>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                {session.status === 'excluded' ? (
                                  <span className="text-gray-500">{session.error_reason}</span>
                                ) : (
                                  `${Math.floor(session.pure_study_minutes / 60)}시간 ${session.pure_study_minutes % 60}분`
                                )}
                              </td>
                              <td className="px-4 py-2 whitespace-nowrap">
                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                  session.status === 'active' ? 'bg-green-100 text-green-800' :
                                  session.status === 'excluded' ? 'bg-gray-100 text-gray-600' :
                                  'bg-blue-100 text-blue-800'
                                }`}>
                                  {session.status === 'active' ? '진행중' : session.status === 'excluded' ? '미계산' : '완료'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <CalendarIcon className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                      <p className="text-gray-500 text-sm">해당 날짜의 학습 세션이 없습니다</p>
                    </div>
                  )}
                </div>
              )}
            </div>

          </div>
        ) : (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <AcademicCapIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">학생을 선택하면 학습 분석을 확인할 수 있습니다</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudyAnalyticsPage;
